<script setup>
/**
 * Loading placeholder. Replaces the `bg-n-slate-3 animate-pulse` idiom that is
 * copy-pasted across the dashboard.
 *
 * Sizing comes from the consumer via `class` (e.g. `class="w-20 h-9"`), matching
 * how the existing hand-rolled skeletons size themselves.
 *
 * Uses `animate-pulse`, the dominant existing idiom. `animate-loader-pulse` (a
 * repo keyframe used by BaseHeatmap) is deliberately not used here so that
 * skeletons animate consistently.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  variant: {
    type: String,
    default: 'block',
    validator: value => ['text', 'block', 'circle'].includes(value),
  },
  /** Number of lines to render. `text` variant only. */
  lines: {
    type: Number,
    default: 1,
  },
  /**
   * Set to false for skeletons inside a container that already announces the
   * loading state, so screen readers hear it once rather than per placeholder.
   */
  announce: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

const VARIANT_CLASSES = {
  text: 'h-4 rounded',
  block: 'rounded-lg',
  circle: 'rounded-full aspect-square',
};

const shapeClass = computed(() => VARIANT_CLASSES[props.variant]);
const lineCount = computed(() =>
  props.variant === 'text' ? Math.max(1, props.lines) : 1
);
</script>

<template>
  <div
    :role="announce ? 'status' : undefined"
    :aria-label="announce ? t('SKELETON.LOADING') : undefined"
    :aria-busy="announce ? 'true' : undefined"
    :class="variant === 'text' && lineCount > 1 ? 'flex flex-col gap-2' : ''"
  >
    <div
      v-for="line in lineCount"
      :key="line"
      aria-hidden="true"
      class="bg-n-slate-3 animate-pulse motion-reduce:animate-none"
      :class="[
        shapeClass,
        // Ragged last line reads as text rather than as a solid block.
        variant === 'text' && lineCount > 1 && line === lineCount
          ? 'w-3/5'
          : 'w-full',
        variant !== 'text' ? 'h-full' : '',
      ]"
    />
  </div>
</template>
